import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Product } from '../services/api';
import apiService from '../services/api';
import { facebookPixel } from '../utils/facebookPixel';

export interface CartItem {
  key: string; // productId + variantId/size if any
  productId: number;
  name: string;
  image?: string;
  unitPrice: number; // final price used in cart
  compareAtPrice?: number; // for strike-through display
  quantity: number;
  inStock: boolean;
  stockQuantity?: number; // maximum allowed quantity based on stock
  variantId?: number; // if applicable
  sizeName?: string; // if applicable
  colorName?: string; // if applicable
  colorCode?: string; // if applicable
}

interface CartContextValue {
  items: CartItem[];
  totalQuantity: number;
  subtotal: number;
  addToCart: (
    product: Product,
    quantity?: number,
    opts?: { variantId?: number; sizeName?: string; unitPrice?: number; compareAtPrice?: number; inStock?: boolean; colorName?: string; colorCode?: string }
  ) => void;
  updateQuantity: (key: string, quantity: number) => void;
  removeItem: (key: string) => void;
  clearCart: () => void;
  stockError: { message: string; productName?: string; availableStock?: number } | null;
  clearStockError: () => void;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

const getStorageKey = (userId?: string | number) => {
  return userId ? `cart_items_v1_user_${userId}` : 'cart_items_v1_guest';
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUserId, setCurrentUserId] = useState<string | number | null>(() => {
    try {
      const user = localStorage.getItem('user');
      return user ? JSON.parse(user)?.id : null;
    } catch {
      return null;
    }
  });

  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const storageKey = getStorageKey(currentUserId || undefined);
      const raw = localStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as CartItem[]) : [];
    } catch {
      return [];
    }
  });

  const [stockError, setStockError] = useState<{ message: string; productName?: string; availableStock?: number } | null>(null);

  useEffect(() => {
    const storageKey = getStorageKey(currentUserId || undefined);
    localStorage.setItem(storageKey, JSON.stringify(items));
  }, [items, currentUserId]);

  // Listen for auth state changes and update cart accordingly
  useEffect(() => {
    const handleAuthStateChange = () => {
      try {
        const user = localStorage.getItem('user');
        const newUserId = user ? JSON.parse(user)?.id : null;
        
        if (newUserId !== currentUserId) {
          // User changed, load their cart
          setCurrentUserId(newUserId);
          
          if (newUserId) {
            // User logged in, load their saved cart
            const newStorageKey = getStorageKey(newUserId);
            const raw = localStorage.getItem(newStorageKey);
            const newItems = raw ? (JSON.parse(raw) as CartItem[]) : [];
            setItems(newItems);
          } else {
            // User logged out, show empty cart but keep their data saved
            // Their cart will be restored when they log back in
            setItems([]);
          }
        }
      } catch (error) {
        console.error('Error handling auth state change:', error);
        // On error, show empty cart but don't clear saved data
        setCurrentUserId(null);
        setItems([]);
      }
    };

    // Listen for auth state changes (login/logout)
    window.addEventListener('authStateChanged', handleAuthStateChange);
    
    // Also listen for storage changes (logout from another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user' || e.key === 'token') {
        handleAuthStateChange();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('authStateChanged', handleAuthStateChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [currentUserId]);

  const calcFinalPrice = (p: Product): { final: number; compare?: number } => {
    const promoPrice = typeof p.promotional_price === 'number' && p.promotional_price > 0 ? p.promotional_price : undefined;
    const discounted = typeof p.discounted_price === 'number' && p.discounted_price > 0 ? p.discounted_price : undefined;
    const hasPromo = Boolean(p.has_promotion || (promoPrice && promoPrice < p.price) || (discounted && discounted < p.price));
    const final = hasPromo ? (promoPrice ?? discounted ?? p.price) : p.price;
    const compare = !hasPromo && p.compare_at_price && p.compare_at_price > p.price ? p.compare_at_price : (hasPromo ? p.price : undefined);
    return { final, compare };
  };

  const buildKey = (productId: number, variantId?: number, sizeName?: string) => `${productId}:${variantId ?? ''}:${sizeName ?? ''}`;

  const addToCart = useCallback(async (product: Product, quantity: number = 1, opts?: { variantId?: number; sizeName?: string; unitPrice?: number; compareAtPrice?: number; inStock?: boolean; colorName?: string; colorCode?: string }) => {
    // Require authentication before adding to cart
    const token = localStorage.getItem('token');
    if (!token) {
      // Redirect unauthenticated users to login page
      window.location.href = '/login';
      return;
    }
    if (!product) return;
    // pick an image
    const image = product.product_images && product.product_images.length > 0 ? product.product_images[0] : product.hover_image;
    // pick first available variant size if present
    let chosenVariantId: number | undefined = opts?.variantId;
    let chosenSizeName: string | undefined = opts?.sizeName;
    let inStock = opts?.inStock ?? true;
    let chosenColorName: string | undefined = opts?.colorName;
    let chosenColorCode: string | undefined = opts?.colorCode;
    let stockQty: number | undefined = undefined;
    if (!chosenVariantId && product.variants && product.variants.length > 0) {
      outer: for (const v of product.variants) {
        if (v?.sizes && v.sizes.length > 0) {
          for (const s of v.sizes) {
            if ((s.stock_quantity ?? 0) > 0) {
              chosenVariantId = v.id as any;
              chosenSizeName = s.name as any;
              inStock = true;
              chosenColorName = v.color as any;
              chosenColorCode = (v as any).color_code as any;
              stockQty = s.stock_quantity as any;
              break outer;
            }
          }
        }
      }
      if (chosenVariantId === undefined) {
        const v = product.variants[0];
        const s = v?.sizes?.[0];
        chosenVariantId = v?.id as any;
        chosenSizeName = s?.name as any;
        inStock = Boolean(s && (s.stock_quantity ?? 0) > 0);
        chosenColorName = (v as any)?.color as any;
        chosenColorCode = (v as any)?.color_code as any;
        stockQty = s?.stock_quantity as any;
      }
      // If variant and size were provided in opts, determine stock based on them
    } else if (product.variants && product.variants.length > 0 && (chosenVariantId || chosenSizeName)) {
      const v = chosenVariantId
        ? product.variants.find(vv => (vv.id as any) === chosenVariantId)
        : product.variants.find(vv => (vv.color as any) === chosenColorName);
      const s = v?.sizes?.find(ss => ss.name === (chosenSizeName as any));
      if (s) {
        stockQty = s.stock_quantity as any;
        inStock = (s.stock_quantity ?? 0) > 0;
      }
    } else if (!product.variants || product.variants.length === 0) {
      // no variants; use product.in_stock
      const simpleStock = typeof (product as any).in_stock === 'number' ? (product as any).in_stock : undefined;
      stockQty = simpleStock;
      inStock = opts?.inStock ?? Boolean(simpleStock === undefined ? true : simpleStock > 0);
    }

    const computed = calcFinalPrice(product);
    const unitPrice = opts?.unitPrice ?? computed.final;
    const compareAt = opts?.compareAtPrice ?? computed.compare;
    const key = buildKey(product.id, chosenVariantId, chosenSizeName);

    // Reserve stock on backend if variant and size are available
    if (chosenVariantId && chosenSizeName) {
      try {
        const maxQty = stockQty && stockQty > 0 ? stockQty : undefined;
        const desiredQty = maxQty ? Math.min(quantity, maxQty) : quantity;
        
        // Check if item already exists in cart
        const existingItem = items.find(i => i.key === key);
        const quantityToReserve = existingItem ? desiredQty : desiredQty;
        
        await apiService.reserveStock([{
          variant_id: chosenVariantId,
          size_name: chosenSizeName,
          quantity: quantityToReserve
        }]);
        
        // If reservation successful, update cart
        setItems(prev => {
          const idx = prev.findIndex(i => i.key === key);
          if (idx >= 0) {
            const next = [...prev];
            const current = next[idx];
            const newQty = maxQty ? Math.min(current.quantity + desiredQty, maxQty) : current.quantity + desiredQty;
            next[idx] = { ...current, quantity: newQty, stockQuantity: maxQty ?? current.stockQuantity };
            
            // Track Facebook Pixel AddToCart event
            facebookPixel.trackAddToCart({
              content_name: product.name,
              content_category: typeof product.category === 'string' ? product.category : product.category?.name,
              content_ids: [product.id.toString()],
              content_type: 'product',
              value: unitPrice * desiredQty,
              currency: 'MMK'
            });
            
            return next;
          }
          
          // Track Facebook Pixel AddToCart event for new item
          facebookPixel.trackAddToCart({
            content_name: product.name,
            content_category: typeof product.category === 'string' ? product.category : product.category?.name,
            content_ids: [product.id.toString()],
            content_type: 'product',
            value: unitPrice * desiredQty,
            currency: 'MMK'
          });
          
          return [
            ...prev,
            {
              key,
              productId: product.id,
              name: product.name,
              image,
              unitPrice: unitPrice,
              compareAtPrice: compareAt,
              quantity: desiredQty,
              inStock,
              stockQuantity: maxQty,
              variantId: chosenVariantId,
              sizeName: chosenSizeName,
              colorName: chosenColorName,
              colorCode: chosenColorCode,
            },
          ];
        });
      } catch (error) {
        console.error('Failed to reserve stock:', error);
        // Show error message to user
        setStockError({
          message: 'Unable to add item to cart. Stock may not be available.',
          productName: product.name,
          availableStock: stockQty
        });
        return;
      }
    } else {
      // Fallback for products without variants (legacy support)
      setItems(prev => {
        const maxQty = stockQty && stockQty > 0 ? stockQty : undefined;
        const desiredQty = maxQty ? Math.min(quantity, maxQty) : quantity;
        const idx = prev.findIndex(i => i.key === key);
        if (idx >= 0) {
          const next = [...prev];
          const current = next[idx];
          const newQty = maxQty ? Math.min(current.quantity + desiredQty, maxQty) : current.quantity + desiredQty;
          next[idx] = { ...current, quantity: newQty, stockQuantity: maxQty ?? current.stockQuantity };
          
          // Track Facebook Pixel AddToCart event
          facebookPixel.trackAddToCart({
            content_name: product.name,
            content_category: typeof product.category === 'string' ? product.category : product.category?.name,
            content_ids: [product.id.toString()],
            content_type: 'product',
            value: unitPrice * desiredQty,
            currency: 'MMK'
          });
          
          return next;
        }
        
        // Track Facebook Pixel AddToCart event for new item
        facebookPixel.trackAddToCart({
          content_name: product.name,
          content_category: typeof product.category === 'string' ? product.category : product.category?.name,
          content_ids: [product.id.toString()],
          content_type: 'product',
          value: unitPrice * desiredQty,
          currency: 'MMK'
        });
        
        return [
          ...prev,
          {
            key,
            productId: product.id,
            name: product.name,
            image,
            unitPrice: unitPrice,
            compareAtPrice: compareAt,
            quantity: desiredQty,
            inStock,
            stockQuantity: maxQty,
            variantId: chosenVariantId,
            sizeName: chosenSizeName,
            colorName: chosenColorName,
            colorCode: chosenColorCode,
          },
        ];
      });
    }
  }, [items]);

  const updateQuantity = useCallback(async (key: string, quantity: number) => {
    const item = items.find(i => i.key === key);
    if (!item || quantity <= 0) return;
    
    // Update reserved stock on backend if variant and size are available
    if (item.variantId && item.sizeName) {
      try {
        await apiService.updateReservedStock(
          item.variantId,
          item.sizeName,
          item.quantity,
          quantity
        );
        
        // If update successful, update cart
        setItems(prev => {
          return prev.map(i => {
            if (i.key !== key) return i;
            const max = i.stockQuantity && i.stockQuantity > 0 ? i.stockQuantity : undefined;
            const clamped = max ? Math.min(quantity, max) : quantity;
            return { ...i, quantity: clamped };
          });
        });
      } catch (error) {
        console.error('Failed to update reserved stock:', error);
        const currentItem = items.find(i => i.key === key);
        setStockError({
          message: 'Unable to update quantity. Stock may not be available.',
          productName: currentItem?.name,
          availableStock: currentItem?.stockQuantity
        });
      }
    } else {
      // Fallback for products without variants
      setItems(prev => {
        return prev.map(i => {
          if (i.key !== key) return i;
          const max = i.stockQuantity && i.stockQuantity > 0 ? i.stockQuantity : undefined;
          const clamped = max ? Math.min(quantity, max) : quantity;
          return { ...i, quantity: clamped };
        });
      });
    }
  }, [items]);

  const removeItem = useCallback(async (key: string) => {
    const item = items.find(i => i.key === key);
    if (!item) return;
    
    // Release reserved stock on backend if variant and size are available
    if (item.variantId && item.sizeName) {
      try {
        await apiService.releaseStock([{
          variant_id: item.variantId,
          size_name: item.sizeName,
          quantity: item.quantity
        }]);
      } catch (error) {
        console.error('Failed to release stock:', error);
        // Continue with removal even if backend call fails
      }
    }
    
    setItems(prev => prev.filter(i => i.key !== key));
  }, [items]);

  const clearStockError = useCallback(() => {
    setStockError(null);
  }, []);

  const clearCart = useCallback(async () => {
    // Release all reserved stock before clearing cart
    const stockItems = items
      .filter(item => item.variantId && item.sizeName)
      .map(item => ({
        variant_id: item.variantId!,
        size_name: item.sizeName!,
        quantity: item.quantity
      }));
    
    if (stockItems.length > 0) {
      try {
        await apiService.releaseStock(stockItems);
      } catch (error) {
        console.error('Failed to release stock when clearing cart:', error);
        // Continue with clearing even if backend call fails
      }
    }
    
    setItems([]);
    // Also clear from localStorage when manually clearing cart
    if (currentUserId) {
      const storageKey = getStorageKey(currentUserId);
      localStorage.removeItem(storageKey);
    }
  }, [items, currentUserId]);

  const subtotal = useMemo(() => items.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0), [items]);
  const totalQuantity = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);

  const value: CartContextValue = {
    items,
    totalQuantity,
    subtotal,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart,
    stockError,
    clearStockError,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
